/**
 * Canonical URL helpers.
 *
 * The site canonicalizes to https://www.compaire.co — the apex redirects to www
 * at the Vercel domain level, so every absolute URL the app emits (canonical
 * links, og:url, JSON-LD) should already be on www rather than sending crawlers
 * and social scrapers through a redirect.
 *
 * Keep this host in sync with `ORIGIN` in scripts/generate-sitemap.mjs, which
 * can't import from src/ (it runs in plain Node before the Vite build).
 */

/** Canonical origin for all absolute URLs the app emits. */
export const SITE_ORIGIN = "https://www.compaire.co";

/** Absolute canonical URL for a route path: "/about" → https://www.compaire.co/about */
export function canonicalUrl(path: string): string {
  return `${SITE_ORIGIN}${path}`;
}

/**
 * Site-wide <head> defaults, mirroring the static tags in index.html.
 *
 * index.html stays the fallback for every route that is not prerendered, so
 * keep the two in sync. CanonicalTags falls back to these rather than omitting
 * the tags, so that every public route emits a complete head — which is what
 * lets client-side navigation replace the previous route's tags instead of
 * leaving them in place. See the note in CanonicalTags for the full story.
 */
export const SITE_DEFAULTS = {
  /** Homepage search title and meta description (also the SPA shell's default). */
  title: "Compaire | Compare Real Estate Teams & Brokerages",
  description:
    "Explore real estate teams and brokerages. Compare culture, training and support, then reach out when you're ready. Free for agents.",
  /**
   * Social scrapers get their own title and a shorter line than the meta
   * description. These are the homepage's approved sharing copy and are kept
   * separate from the search title and meta description above.
   */
  socialTitle: "Compaire — Get to know your next team or brokerage",
  socialDescription: "Compare what matters. Get to know teams and brokerages, then reach out when you're ready.",
} as const;
